import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Typography, Alert, tooltipClasses, SelectChangeEvent } from '@mui/material'
import { useFocusState, useGratifications } from '../../../../../../utils/hooks'
import GratificationField from './GratificationField'
import CustomButton from '../../../../../v2/general/CustomButton/CustomButton'
import { Add } from '@mui/icons-material'
import CustomSelect from '../../../../../v2/general/CustomSelect/CustomSelect'
import { LightTooltip } from '../../../../../LighTooltip'
import { CompanyGratificationParam } from '../../../../../../types/gratifications'
import CloseIcon from '@mui/icons-material/Close'

const ALWAYS_VISIBLE_SP_FIELDS = ['@AlugueresOutras', '@AlugueresFolgaAluguer'] as const

const normalizeFieldName = (field: string): string => field.replace('@', '').toLowerCase()

const Gratifications: React.FC = () => {
	const {
		gratificationParams,
		blockedGratifications,
		handleGratificationValue,
		handleAddGratification,
		isLoading,
		isError,
		onRetry,
	} = useGratifications()

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, { getFieldValue, setFieldFocus, clearFocus }] = useFocusState()
	const [showNewField, setShowNewField] = useState<boolean>(false)
	const [newFieldValue, setNewFieldValue] = useState<string>('')
	const [refundsToShow, setRefundsToShow] = useState<CompanyGratificationParam[]>([])

	const onAdd = () => {
		const selected = gratificationParams.find(g => g.id === Number(newFieldValue))
		if (selected) {
			setRefundsToShow(prev => [...prev, selected])
			handleAddGratification(selected)
		}
		setShowNewField(false)
		setNewFieldValue('')
	}

	useEffect(() => {
		setRefundsToShow(previousRefunds => {
			const previouslyVisibleFields = new Set(previousRefunds.map(refund => normalizeFieldName(refund.campoSP)))

			return gratificationParams.filter(gratification => {
				if (
					ALWAYS_VISIBLE_SP_FIELDS.includes(gratification.campoSP as (typeof ALWAYS_VISIBLE_SP_FIELDS)[number]) &&
					(gratification.descricao === 'Transferes' || gratification.descricao === 'Outros Abonos')
				) {
					return true
				}

				return previouslyVisibleFields.has(normalizeFieldName(gratification.campoSP)) || gratification.valor > 0
			})
		})
	}, [gratificationParams])

	const gratificationFields = useMemo(() => {
		if (refundsToShow.length === 0 && !showNewField) {
			return (
				<Typography
					variant='body1'
					sx={{
						fontFamily: 'var(--font-family)',
						fontSize: '0.875rem',
						fontWeight: 600,
						lineHeight: '157%',
						color: 'var(--primary-light)',
						marginBottom: '4px',
					}}
				>
					Sem Gratificações Aplicáveis
				</Typography>
			)
		}
		return refundsToShow.map(grat => (
			<Box
				key={`${grat.id}-${grat.campoSP}`}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 4,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 1,
					}}
				>
					<GratificationField
						gratification={grat}
						value={getFieldValue(grat.valor, `${grat.id}-${grat.campoSP}`)}
						isBlocked={blockedGratifications.includes(grat.descricao)}
						onValueChange={value => handleGratificationValue(value, grat)}
						onFocus={() => setFieldFocus(`${grat.id}-${grat.campoSP}`, grat.valor)}
						onBlur={clearFocus}
					/>
					<LightTooltip
						title={`Remover ${grat.descricao}`}
						placement='top-start'
						sx={{
							[`& .${tooltipClasses.tooltip}`]: {
								transform: 'translate(1.5rem,-0.25rem) !important',
							},
						}}
					>
						<CustomButton
							aria-label={`Remover ${grat.descricao}`}
							variant='text'
							color='error'
							size='small'
							onClick={() => {
								setRefundsToShow(prev => prev.filter(g => g.id !== grat.id))
								handleGratificationValue(0, grat)
							}}
							sx={{
								padding: '4px',
								fontSize: '0.75rem',
								width: 'fit-content',
								minWidth: 0,
							}}
						>
							<CloseIcon
								fontSize='small'
								sx={{ color: 'var(--primary)' }}
								aria-hidden='true'
							/>
						</CustomButton>
					</LightTooltip>
				</Box>
			</Box>
		))
	}, [
		refundsToShow,
		showNewField,
		blockedGratifications,
		handleGratificationValue,
		getFieldValue,
		setFieldFocus,
		clearFocus,
	])

	if (isLoading) {
		return <CircularProgress />
	}

	return (
		<Box
			sx={{
				display: 'grid',
			}}
		>
			<Typography
				variant='subtitle2'
				sx={{
					fontFamily: 'var(--font-family)',
					fontWeight: 600,
					fontSize: '0.875rem',
					lineHeight: '175%',
					color: 'var(--primary-dark)',
					paddingBlock: 3,
				}}
			>
				Outros Abonos
			</Typography>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'space-between',
					gap: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'flex-start',
						gap: 2,
					}}
				>
					{gratificationFields}
					{isError && (
						<Alert
							severity='error'
							sx={{
								mb: 2,
								width: '100%',
								height: '37px',
								padding: '0 16px',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								fontFamily: 'var(--font-family)',
								fontSize: '14px',
								fontWeight: 500,
								letterSpacing: '0.15px',
								lineHeight: '150%',
							}}
						>
							Não foi possível carregar os campos de gratificação
							<Button
								onClick={onRetry}
								sx={{ ml: 2 }}
							>
								Tentar novamente
							</Button>
						</Alert>
					)}

					{showNewField && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
							<CustomSelect
								options={gratificationParams
									.filter(g => !refundsToShow.some(r => r.id === g.id))
									.map(g => ({
										label: g.descricao,
										value: g.id.toString(),
									}))}
								size='small'
								label='Adicione Abono'
								value={newFieldValue}
								onChange={e => setNewFieldValue((e as SelectChangeEvent<string | string[]>).target.value as string)}
								onKeyDown={e => {
									if (e.key === 'Escape') {
										setShowNewField(false)
										setNewFieldValue('')
									}
								}}
								autoFocus
							/>
							<CustomButton
								variant='text'
								color={gratificationParams.find(g => g.id === Number(newFieldValue)) ? 'primary' : 'warning'}
								onClick={onAdd}
							>
								{gratificationParams.find(g => g.id === Number(newFieldValue)) ? 'Guardar' : 'Fechar'}
							</CustomButton>
						</Box>
					)}
				</Box>
				{!showNewField && (
					<CustomButton
						size='small'
						variant='text'
						endIcon={<Add />}
						onClick={() => setShowNewField(true)}
					>
						Adicionar Abono
					</CustomButton>
				)}
			</Box>
		</Box>
	)
}

export default Gratifications
